/**
 * Filter editor — renders ungrouped filter rows plus zero-or-more
 * AND/OR groups, mirroring the legacy "Filters" tab on
 * EditReportMain.aspx.
 *
 * Each row is `[Field] [Operator] [Value...] [×]`. The operator list is
 * keyed off the field's `data_type` and resolved via
 * `useOperatorCatalog`. Date fields delegate the operator dropdown to
 * `DateOperatorSelect`.
 */

import { Plus, Trash2, FolderPlus } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import {
  type BuilderDataset,
  type BuilderFieldDef,
  type BuilderFilter,
  type BuilderFilterGroup,
  type DsidCatalogResponse,
  type FieldDataType,
  type IndicatorBuilderConfig,
  type OperatorCatalogResponse,
  type OperatorEntry,
} from '../../../lib/api/reporting';
import { DateOperatorSelect } from './DateOperatorSelect';
import { lookupField } from './AxisDesignPanel';

interface FilterEditorProps {
  config: IndicatorBuilderConfig;
  onChange: (next: IndicatorBuilderConfig) => void;
  catalog: DsidCatalogResponse | null;
  operatorCatalog: OperatorCatalogResponse | null;
}

/**
 * Returns the operator list applicable to a particular data type.
 */
function operatorsForType(
  catalog: OperatorCatalogResponse | null,
  dataType: FieldDataType | undefined,
): OperatorEntry[] {
  if (!catalog) return [];
  switch (dataType) {
    case 'string':
    case 'enum':
      return [...(catalog.text || []), ...(catalog.enum || [])];
    case 'int':
    case 'decimal':
      return [...(catalog.number || [])];
    case 'date':
      return [...(catalog.date || [])];
    case 'datetime':
      return [...(catalog.datetime || catalog.date || [])];
    case 'bool':
      return [...(catalog.bool || [])];
    default:
      return [];
  }
}

/** Flat list of every BuilderFieldDef for the field-pickers below. */
function flatFields(catalog: DsidCatalogResponse | null): BuilderFieldDef[] {
  if (!catalog) return [];
  const out: BuilderFieldDef[] = [];
  for (const ds of catalog.datasets) {
    for (const f of ds.fields) {
      out.push(f);
    }
  }
  return out;
}

interface FilterRowProps {
  filter: BuilderFilter;
  catalog: DsidCatalogResponse | null;
  operatorCatalog: OperatorCatalogResponse | null;
  fieldOptions: BuilderFieldDef[];
  onChange: (next: BuilderFilter) => void;
  onRemove: () => void;
}

function FilterRow({
  filter,
  catalog,
  operatorCatalog,
  fieldOptions,
  onChange,
  onRemove,
}: FilterRowProps) {
  const fieldDef = lookupField(catalog, filter.field_dsid, filter.field_id);
  const dataType = filter.field_data_type || fieldDef?.data_type;
  const isDateLike = dataType === 'date' || dataType === 'datetime';
  const operators = operatorsForType(operatorCatalog, dataType);
  const fieldKey = `${filter.field_dsid}-${filter.field_id}`;

  const handleFieldChange = (key: string) => {
    const [dsidStr, fidStr] = key.split('-');
    const dsid = parseInt(dsidStr ?? '0', 10);
    const fid = parseInt(fidStr ?? '0', 10);
    const target = fieldOptions.find((f) => f.dsid === dsid && f.field_id === fid);
    if (!target) return;
    onChange({
      ...filter,
      field_dsid: target.dsid,
      field_id: target.field_id,
      field_data_type: target.data_type,
      operator: '',
      value: {},
    });
  };

  const setOperator = (op: string) => onChange({ ...filter, operator: op });
  const setValue = (next: Record<string, unknown>) => onChange({ ...filter, value: next });

  const renderValueInput = () => {
    if (!filter.operator) {
      return <Input disabled placeholder="Pick an operator" className="h-8 text-xs" />;
    }
    if (isDateLike) {
      // Most relative-date operators take no value; keep an exact_period fallback.
      if (filter.operator === 'exact_period') {
        const start = typeof filter.value['start'] === 'string' ? filter.value['start'] : '';
        const end = typeof filter.value['end'] === 'string' ? filter.value['end'] : '';
        return (
          <div className="grid grid-cols-2 gap-1">
            <Input
              type="date"
              className="h-8 text-xs"
              value={start}
              onChange={(e) => setValue({ ...filter.value, start: e.target.value })}
            />
            <Input
              type="date"
              className="h-8 text-xs"
              value={end}
              onChange={(e) => setValue({ ...filter.value, end: e.target.value })}
            />
          </div>
        );
      }
      return <Input disabled placeholder="No value required" className="h-8 text-xs" />;
    }

    if (dataType === 'int' || dataType === 'decimal') {
      if (filter.operator === 'between') {
        const start = typeof filter.value['start'] === 'number' ? filter.value['start'] : '';
        const end = typeof filter.value['end'] === 'number' ? filter.value['end'] : '';
        return (
          <div className="grid grid-cols-2 gap-1">
            <Input
              type="number"
              className="h-8 text-xs"
              value={start}
              onChange={(e) => setValue({ ...filter.value, start: parseFloat(e.target.value) || 0 })}
            />
            <Input
              type="number"
              className="h-8 text-xs"
              value={end}
              onChange={(e) => setValue({ ...filter.value, end: parseFloat(e.target.value) || 0 })}
            />
          </div>
        );
      }
      const v = typeof filter.value['value'] === 'number' ? filter.value['value'] : '';
      return (
        <Input
          type="number"
          className="h-8 text-xs"
          value={v}
          onChange={(e) => setValue({ value: parseFloat(e.target.value) || 0 })}
        />
      );
    }

    if (dataType === 'bool') {
      const v = typeof filter.value['value'] === 'boolean' ? String(filter.value['value']) : '';
      return (
        <Select value={v || undefined} onValueChange={(next) => setValue({ value: next === 'true' })}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="True/False" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="true">True</SelectItem>
            <SelectItem value="false">False</SelectItem>
          </SelectContent>
        </Select>
      );
    }

    if (dataType === 'enum' && fieldDef && fieldDef.enum_choices.length > 0) {
      const v = typeof filter.value['value'] === 'string' ? filter.value['value'] : '';
      return (
        <Select value={v || undefined} onValueChange={(next) => setValue({ value: next })}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Choose value" />
          </SelectTrigger>
          <SelectContent>
            {fieldDef.enum_choices.map((c) => (
              <SelectItem key={String(c.value)} value={String(c.value)}>
                {c.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    // Default: text + multi-select via <|> separator.
    const text = typeof filter.value['value'] === 'string' ? filter.value['value'] : '';
    return (
      <Input
        type="text"
        className="h-8 text-xs"
        placeholder={filter.operator === 'is' || filter.operator === 'is_not' ? 'val1<|>val2' : ''}
        value={text}
        onChange={(e) => setValue({ value: e.target.value })}
      />
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center px-2 py-1.5 border border-border rounded">
      <div className="md:col-span-4">
        <Select value={fieldKey === '0-0' ? undefined : fieldKey} onValueChange={handleFieldChange}>
          <SelectTrigger className="h-8 text-xs">
            <SelectValue placeholder="Pick a field" />
          </SelectTrigger>
          <SelectContent>
            {fieldOptions.map((f) => (
              <SelectItem key={`${f.dsid}-${f.field_id}`} value={`${f.dsid}-${f.field_id}`}>
                {f.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-3">
        {isDateLike ? (
          <DateOperatorSelect
            value={filter.operator}
            onChange={setOperator}
            catalog={operatorCatalog}
            variant={dataType === 'date' ? 'date' : 'datetime'}
          />
        ) : (
          <Select value={filter.operator || undefined} onValueChange={setOperator}>
            <SelectTrigger className="h-8 text-xs">
              <SelectValue placeholder="Operator" />
            </SelectTrigger>
            <SelectContent>
              {operators.length === 0 ? (
                <SelectItem value="none" disabled>No operators</SelectItem>
              ) : (
                operators.map((op) => (
                  <SelectItem key={op.key} value={op.key}>{op.label}</SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
        )}
      </div>
      <div className="md:col-span-4">{renderValueInput()}</div>
      <div className="md:col-span-1 flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onRemove}
          aria-label="Remove filter"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

interface GroupBlockProps {
  group: BuilderFilterGroup;
  index: number;
  catalog: DsidCatalogResponse | null;
  operatorCatalog: OperatorCatalogResponse | null;
  fieldOptions: BuilderFieldDef[];
  onChange: (next: BuilderFilterGroup) => void;
  onRemove: () => void;
}

function GroupBlock({
  group,
  index,
  catalog,
  operatorCatalog,
  fieldOptions,
  onChange,
  onRemove,
}: GroupBlockProps) {
  const updateRow = (idx: number, next: BuilderFilter) => {
    onChange({
      ...group,
      filters: group.filters.map((row, i) => (i === idx ? next : row)),
    });
  };
  const removeRow = (idx: number) => {
    onChange({ ...group, filters: group.filters.filter((_, i) => i !== idx) });
  };
  const addRow = () => {
    const blank: BuilderFilter = {
      field_dsid: 0,
      field_id: 0,
      operator: '',
      value: {},
    };
    onChange({ ...group, filters: [...group.filters, blank] });
  };

  return (
    <div className="border border-dashed border-primary/40 rounded p-2 space-y-2 bg-primary/5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
            Group {index + 1}
          </Label>
          <Select
            value={group.group_op}
            onValueChange={(v) => onChange({ ...group, group_op: v === 'OR' ? 'OR' : 'AND' })}
          >
            <SelectTrigger className="h-7 w-[80px] text-[11px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AND">AND</SelectItem>
              <SelectItem value="OR">OR</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          className="h-7 text-xs text-muted-foreground hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5 mr-1" />
          Remove group
        </Button>
      </div>
      {group.filters.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic px-1">
          No filters in this group yet.
        </div>
      ) : (
        <div className="space-y-1.5">
          {group.filters.map((f, i) => (
            <FilterRow
              key={`grp-${index}-row-${i}`}
              filter={f}
              catalog={catalog}
              operatorCatalog={operatorCatalog}
              fieldOptions={fieldOptions}
              onChange={(next) => updateRow(i, next)}
              onRemove={() => removeRow(i)}
            />
          ))}
        </div>
      )}
      <Button type="button" size="sm" variant="outline" onClick={addRow} className="h-7 text-xs">
        <Plus className="h-3.5 w-3.5 mr-1" />
        Add filter
      </Button>
    </div>
  );
}

export function FilterEditor({ config, onChange, catalog, operatorCatalog }: FilterEditorProps) {
  const filters: BuilderFilter[] = config.filters || [];
  const groups: BuilderFilterGroup[] = config.filter_groups || [];
  const fieldOptions = flatFields(catalog);

  const updateFilter = (idx: number, next: BuilderFilter) => {
    const nextList = filters.map((row, i) => (i === idx ? next : row));
    onChange({ ...config, filters: nextList });
  };
  const removeFilter = (idx: number) => {
    const nextList = filters.filter((_, i) => i !== idx);
    onChange({ ...config, filters: nextList });
  };
  const addFilter = () => {
    const blank: BuilderFilter = {
      field_dsid: 0,
      field_id: 0,
      operator: '',
      value: {},
    };
    onChange({ ...config, filters: [...filters, blank] });
  };

  const updateGroup = (idx: number, next: BuilderFilterGroup) => {
    const nextList = groups.map((g, i) => (i === idx ? next : g));
    onChange({ ...config, filter_groups: nextList });
  };
  const removeGroup = (idx: number) => {
    const nextList = groups.filter((_, i) => i !== idx);
    onChange({ ...config, filter_groups: nextList });
  };
  const addGroup = () => {
    const blank: BuilderFilterGroup = { group_op: 'AND', filters: [] };
    onChange({ ...config, filter_groups: [...groups, blank] });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Filters</Label>
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="ghost" onClick={addFilter} className="h-7 text-xs">
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add filter
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={addGroup} className="h-7 text-xs">
            <FolderPlus className="h-3.5 w-3.5 mr-1" />
            Add group
          </Button>
        </div>
      </div>
      {filters.length === 0 && groups.length === 0 ? (
        <div className="text-xs text-muted-foreground italic px-2">
          No filters applied. The chart will use every record in the dataset.
        </div>
      ) : null}
      {filters.length > 0 && (
        <div className="space-y-1.5">
          {filters.map((f, i) => (
            <FilterRow
              key={`top-${i}`}
              filter={f}
              catalog={catalog}
              operatorCatalog={operatorCatalog}
              fieldOptions={fieldOptions}
              onChange={(next) => updateFilter(i, next)}
              onRemove={() => removeFilter(i)}
            />
          ))}
        </div>
      )}
      {groups.length > 0 && (
        <div className="space-y-2">
          {groups.map((g, i) => (
            <GroupBlock
              key={`grp-${i}`}
              group={g}
              index={i}
              catalog={catalog}
              operatorCatalog={operatorCatalog}
              fieldOptions={fieldOptions}
              onChange={(next) => updateGroup(i, next)}
              onRemove={() => removeGroup(i)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/** Re-export the flat-fields helper for tests. */
export { flatFields, operatorsForType };
/** Re-export type for callers that build their own UI. */
export type { BuilderDataset };
